// LoRaWAN page logic (WP9): radio and battery levels, stale devices, which
// ChirpStack applications can be mapped to a site.
import type { LorawanApplication, LorawanDevice, LorawanProfiles } from '../api/types.js';

export type Level = 'ok' | 'warn' | 'bad';

/**
 * Link quality from the best gateway's RSSI and SNR. EU868 LoRa demodulates
 * down to about −7.5 dB SNR at SF7 and −20 dB at SF12; RSSI below −115 dBm
 * leaves little margin indoors.
 */
export function signalLevel(rssi: number | null, snr: number | null): Level | null {
  if (rssi === null && snr === null) return null;
  if ((rssi !== null && rssi < -115) || (snr !== null && snr < -10)) return 'bad';
  if ((rssi !== null && rssi < -105) || (snr !== null && snr < 0)) return 'warn';
  return 'ok';
}

/** Battery percent: below 20 % replace soon, below 40 % watch. */
export function batteryLevel(percent: number | null): Level | null {
  if (percent === null) return null;
  if (percent < 20) return 'bad';
  if (percent < 40) return 'warn';
  return 'ok';
}

/** Room sensors send every 10–15 min; silent for longer than this = stale. */
export const STALE_AFTER_MS = 2 * 60 * 60_000;

export function isStale(d: LorawanDevice, now: number, afterMs = STALE_AFTER_MS): boolean {
  return d.last_uplink === null || now - d.last_uplink > afterMs;
}

/** Devices with uplinks first (newest first), then the ones ChirpStack knows without data. */
export function sortDevices(devices: readonly LorawanDevice[]): LorawanDevice[] {
  return [...devices].sort((a, b) => {
    const ta = a.last_uplink ?? -Infinity;
    const tb = b.last_uplink ?? -Infinity;
    if (ta !== tb) return tb - ta;
    return (a.name ?? a.dev_eui).localeCompare(b.name ?? b.dev_eui);
  });
}

export interface ApplicationChoice {
  /** Not mapped to any site. */
  free: LorawanApplication[];
  /** Mapped to another site: mapping here moves the application. */
  elsewhere: LorawanApplication[];
  /** Mapped to this site. */
  here: LorawanApplication[];
}

export function applicationChoice(
  apps: readonly LorawanApplication[],
  siteId: string,
): ApplicationChoice {
  const byName = (a: LorawanApplication, b: LorawanApplication) => a.name.localeCompare(b.name);
  return {
    free: apps.filter((a) => a.site === null).sort(byName),
    elsewhere: apps.filter((a) => a.site !== null && a.site.id !== siteId).sort(byName),
    here: apps.filter((a) => a.site?.id === siteId).sort(byName),
  };
}

/** DevEUI as ChirpStack's UI shows it (lower-case hex). */
export function formatDevEui(eui: string): string {
  return eui.toLowerCase();
}

// --- provisioning: input checks, CSV import ---------------------------------------

/** Vendors print EUIs and keys with `-`, `:` or spaces; the API wants plain hex. */
export function normalizeHex(v: string): string {
  return v.replace(/[\s:-]/g, '').toLowerCase();
}

function hexError(label: string, value: string, digits: number, nonZero: boolean): string | null {
  const v = normalizeHex(value);
  if (v === '') return `${label} is required`;
  if (!new RegExp(`^[0-9a-f]{${String(digits)}}$`).test(v)) {
    return `${label} must be ${String(digits)} hex digits`;
  }
  if (nonZero && !/[1-9a-f]/.test(v)) return `${label} must not be all zeros`;
  return null;
}

export const devEuiError = (v: string) => hexError('DevEUI', v, 16, true);
/** JoinEUI zeros are common and valid. */
export const joinEuiError = (v: string) => hexError('JoinEUI', v, 16, false);
export const appKeyError = (v: string) => hexError('AppKey', v, 32, true);
export const gatewayEuiError = (v: string) => hexError('Gateway EUI', v, 16, true);

export interface SensorInput {
  dev_eui: string;
  join_eui: string;
  app_key: string;
  name: string;
  /** Catalog key or ChirpStack device profile id. */
  profile: string;
}

/** Problems of one sensor (empty = fine). */
export function sensorErrors(s: SensorInput): string[] {
  const out = [devEuiError(s.dev_eui), joinEuiError(s.join_eui), appKeyError(s.app_key)].filter(
    (e): e is string => e !== null,
  );
  if (s.name.trim() === '') out.push('name is required');
  else if (s.name.trim().length > 100) out.push('name is longer than 100 characters');
  if (s.profile === '') out.push('profile is required');
  return out;
}

/** A device profile the UI can offer: catalog entries first, then the tenant's own. */
export interface ProfileOption {
  /** What the API takes: catalog key or profile id. */
  value: string;
  label: string;
}

export function profileOptions(p: LorawanProfiles | undefined): ProfileOption[] {
  if (!p) return [];
  const catalogNames = new Set(p.catalog.map((c) => c.name));
  return [
    ...p.catalog.map((c) => ({ value: c.key, label: c.name })),
    ...p.profiles
      .filter((x) => !catalogNames.has(x.name))
      .map((x) => ({ value: x.id, label: x.name })),
  ];
}

/** CSV profile column → API value: catalog key, catalog or profile name, or profile id. */
export function resolveProfile(text: string, p: LorawanProfiles | undefined): string | null {
  const t = text.trim().toLowerCase();
  if (!p || t === '') return null;
  for (const c of p.catalog) {
    if (c.key === t || c.name.toLowerCase() === t) return c.key;
  }
  for (const x of p.profiles) {
    if (x.id.toLowerCase() === t || x.name.toLowerCase() === t) return x.catalog ?? x.id;
  }
  return null;
}

export interface ImportRow {
  /** 1-based line in the file. */
  line: number;
  sensor: SensorInput;
  errors: string[];
}

/**
 * Parses `DevEUI;JoinEUI;AppKey;name;profile` lines (`,` or tab also work
 * when a line has no `;`). A first line naming the DevEUI column is a header;
 * blank lines and `#` comments are skipped. An empty profile takes
 * `defaultProfile`. Rows are checked for format, duplicates in the file and
 * DevEUIs the site already has.
 */
export function parseSensorCsv(
  text: string,
  opts: {
    profiles: LorawanProfiles | undefined;
    defaultProfile: string;
    existing: ReadonlySet<string>;
  },
): ImportRow[] {
  const rows: ImportRow[] = [];
  const seen = new Map<string, number>();
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/);
  let first = true;
  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (line === '' || line.startsWith('#')) return;
    const sep = line.includes(';') ? ';' : line.includes('\t') ? '\t' : ',';
    const cells = line.split(sep).map((c) =>
      c
        .trim()
        .replace(/^"(.*)"$/, '$1')
        .trim(),
    );
    const header = first && /dev.?eui/i.test(cells[0] ?? '');
    first = false;
    if (header) return;
    const [devEui = '', joinEui = '', appKey = '', name = '', profileText = ''] = cells;
    const profile =
      profileText === '' ? opts.defaultProfile : (resolveProfile(profileText, opts.profiles) ?? '');
    const sensor: SensorInput = {
      dev_eui: normalizeHex(devEui),
      join_eui: normalizeHex(joinEui),
      app_key: normalizeHex(appKey),
      name: name.trim(),
      profile,
    };
    const errors = sensorErrors(sensor).map((e) =>
      e === 'profile is required' && profileText !== '' ? `unknown profile "${profileText}"` : e,
    );
    if (cells.length > 5) errors.push(`${String(cells.length)} columns, expected at most 5`);
    if (devEuiError(sensor.dev_eui) === null) {
      const before = seen.get(sensor.dev_eui);
      if (before !== undefined) errors.push(`DevEUI repeats line ${String(before)}`);
      else seen.set(sensor.dev_eui, i + 1);
      if (opts.existing.has(sensor.dev_eui)) errors.push('DevEUI already at this site');
    }
    rows.push({ line: i + 1, sensor, errors });
  });
  return rows;
}

export type ImportResult = { line: number; dev_eui: string } & (
  { ok: true } | { ok: false; error: string }
);

/** Creates the valid rows one after the other; one failure does not stop the rest. */
export async function importSensors(
  rows: readonly ImportRow[],
  create: (s: SensorInput) => Promise<unknown>,
  onResult?: (r: ImportResult) => void,
): Promise<ImportResult[]> {
  const out: ImportResult[] = [];
  for (const row of rows) {
    if (row.errors.length > 0) continue;
    let r: ImportResult;
    try {
      await create(row.sensor);
      r = { line: row.line, dev_eui: row.sensor.dev_eui, ok: true };
    } catch (e) {
      r = {
        line: row.line,
        dev_eui: row.sensor.dev_eui,
        ok: false,
        error: e instanceof Error ? e.message : String(e),
      };
    }
    out.push(r);
    onResult?.(r);
  }
  return out;
}

/** How the device table shows ChirpStack's state of a device. */
export function deviceState(d: LorawanDevice): { label: string; level: Level | null } {
  if (d.enabled === false) return { label: 'Disabled', level: 'warn' };
  if (d.join_state === 'pending') return { label: 'Waiting for join', level: 'warn' };
  if (d.join_state === 'joined') return { label: 'Joined', level: 'ok' };
  if (d.in_chirpstack === false) return { label: 'Not in ChirpStack', level: 'bad' };
  return { label: '–', level: null };
}

export type LocationResult =
  | { ok: true; value: { latitude: number; longitude: number } | null }
  | { ok: false; error: string };

/** Gateway coordinates from two text fields: both empty = none; else both must be numbers. */
export function parseLocation(lat: string, lon: string): LocationResult {
  if (lat.trim() === '' && lon.trim() === '') return { ok: true, value: null };
  const latitude = Number(lat.trim().replace(',', '.'));
  const longitude = Number(lon.trim().replace(',', '.'));
  if (lat.trim() === '' || !Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
    return { ok: false, error: 'latitude must be a number between −90 and 90' };
  }
  if (lon.trim() === '' || !Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
    return { ok: false, error: 'longitude must be a number between −180 and 180' };
  }
  return { ok: true, value: { latitude, longitude } };
}
